import React from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import theme from "../../utils/theme";
import toast from "react-hot-toast";

const getStyles = (error) => ({
  uploadBox: {
    p: 2,
    bgcolor: theme.palette.background.paper,
    border: `${2.5}px dashed ${
      error ? theme.palette.error.dark : theme.palette.info.main
    }`,
    borderRadius: 3,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    mb: 2,
    mx: 2,
    mt: 2,
    position: "relative",
    transition: "border-color 0.2s, background-color 0.2s",
    boxShadow: 2,
    backgroundColor: error ? "#fff5f5" : theme.palette.background.paper,
    "&:hover": {
      borderColor: error ? theme.palette.error.dark : theme.palette.info.dark,
      bgcolor: error ? "#fff5f5" : theme.palette.background.default,
    },
  },
  fileInput: {
    display: "none",
  },
  imageContainer: {
    position: "relative",
    width: 130,
    height: 130,
    borderRadius: 3,
    border: `1.5px solid ${theme.palette.grey[500]}`,
    overflow: "hidden",
    bgcolor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: 1,
    margin: "0 auto",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    zIndex: 1,
  },
  errorContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    color: theme.palette.error.main,
  },
  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: 2,
  },
  removeButton: {
    position: "absolute",
    top: 8,
    right: 8,
    background: theme.palette.background.paper,
    color: theme.palette.primary.main,
    border: `1.5px solid ${theme.palette.grey[500]}`,
    borderRadius: 1,
    zIndex: 2,
    p: 0.5,
  },
  addIcon: {
    color: theme.palette.secondary.main,
    mb: 1,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.75rem",
    mt: 0.5,
    ml: 1.5,
  },
});

const ImageUploadSection = ({
  handleImageUpload,
  images = [],
  error,
  handleRemoveImage,
}) => {
  const [loadingStates, setLoadingStates] = React.useState({});
  const [errorStates, setErrorStates] = React.useState({});
  const styles = getStyles(error); // Highlight error style if any error

  // Use state to store blob URLs to prevent recreating them on each render
  const [blobUrls, setBlobUrls] = React.useState({});
  // Create preview sources - handles both File objects and URL strings from image objects
  React.useEffect(() => {
    const newBlobUrls = { ...blobUrls };
    let hasChanges = false;

    // Create new blob URLs only for File objects that don't have one yet
    images.forEach((item, index) => {
      const key =
        item instanceof File ? item.name + item.size + item.lastModified : null;
      if (item instanceof File && key && !blobUrls[key]) {
        newBlobUrls[key] = URL.createObjectURL(item);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setBlobUrls(newBlobUrls);
    }

    // Clean up function to revoke all blob URLs when component unmounts
    return () => {
      Object.values(blobUrls).forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [images, blobUrls]); // Recreate URLs when images or blobUrls change

  // Generate preview sources using blob URLs or direct URLs
  const previewSources = images.map((item) => {
    // If it's a File, use the cached/revoked-managed blob URL
    if (item instanceof File) {
      const key = item.name + item.size + item.lastModified;
      return blobUrls[key] || "";
    }

    // If it's an object (non-File), return its image_url
    if (item && typeof item === "object") {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      return baseUrl + item.image_url || "";
    }

    // Otherwise return the primitive value (e.g., a URL string)
    return item;
  });

  const handleImageError = (index) => {
    setErrorStates((prev) => ({ ...prev, [index]: true }));
    setLoadingStates((prev) => ({ ...prev, [index]: false }));

    // If image fails to load, try to reload it once (may help with blob URL issues)
    if (images[index] instanceof File) {
      const item = images[index];
      const key = item.name + item.size + item.lastModified;
      if (blobUrls[key]) {
        // Revoke and recreate the blob URL
        URL.revokeObjectURL(blobUrls[key]);
        setBlobUrls((prev) => ({
          ...prev,
          [key]: URL.createObjectURL(item),
        }));
      }
    }
  };

  const handleImageLoadStart = (index) => {
    setLoadingStates((prev) => ({ ...prev, [index]: true }));
    setErrorStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleImageLoad = (index) => {
    setLoadingStates((prev) => ({ ...prev, [index]: false }));
    setErrorStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleRemoveClick = (e, index) => {
    e.stopPropagation(); // Stop event from bubbling up to parent
    handleRemoveImage(e, index);
  };

  return (
    <>
      <Box
        sx={styles.uploadBox}
        onClick={() => document.getElementById("addcomp-image-input").click()}
      >
        <input
          id="addcomp-image-input"
          type="file"
          accept="image/*"
          multiple
          style={styles.fileInput}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            const projectedCount = images.length + files.length;
            if (projectedCount > 15) toast.error("Maximum 15 images allowed");
            else handleImageUpload(e);
          }}
        />
        {images.length > 0 ? (
          <Grid container spacing={2}>
            {previewSources.map((src, index) => (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                lg={2}
                key={images[index]?.image_id || index}
              >
                <Box sx={styles.imageContainer}>
                  {loadingStates[index] && (
                    <Box sx={styles.loadingOverlay}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  {errorStates[index] ? (
                    <Box sx={styles.errorContainer}>
                      <Typography variant="caption">
                        Preview unavailable
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={src}
                      alt={`Preview ${index + 1}`}
                      sx={styles.previewImage}
                      onLoadStart={() => handleImageLoadStart(index)}
                      onLoad={() => handleImageLoad(index)}
                      onError={() => handleImageError(index)}
                      loading="lazy"
                    />
                  )}
                  <Tooltip title="Remove image" arrow placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => handleRemoveClick(e, index)}
                      sx={styles.removeButton}
                      aria-label={`Remove image ${index + 1}`}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            <AddIcon fontSize="large" sx={styles.addIcon} />
            <Typography variant="h5">Upload up to 15 images</Typography>
          </>
        )}
      </Box>
      {error && (
        <FormHelperText sx={styles.errorText}>
          {error || "At least one image is required"}
        </FormHelperText>
      )}
    </>
  );
};

export default ImageUploadSection;
